import {
  CoordinatorError,
  CustomError,
  CustomErrorName,
  GitRepositoryError,
} from './types';

export type HttpResponseErrorMetadata = {
  httpStatus: number;
  responseBody: unknown;
};

export const getHttpResponseErrorMetadata = (
  error: unknown,
): HttpResponseErrorMetadata | undefined => {
  if (!(error instanceof Error)) return undefined;
  const candidate = error as Error & Partial<HttpResponseErrorMetadata>;
  if (typeof candidate.httpStatus !== 'number') return undefined;
  return {
    httpStatus: candidate.httpStatus,
    responseBody: candidate.responseBody,
  };
};

/**
 * Keeps HTTP context on the existing application error. The error conversion
 * below intentionally preserves established error classes and messages, but
 * would otherwise discard the response status and body. Attaching these fields
 * lets callers that need structured diagnostics inspect them without changing
 * the behavior of existing `catch` blocks or defining endpoint-specific errors.
 */
const attachHttpResponseMetadata = <T extends Error>(
  error: T,
  response: Response,
  responseBody: unknown,
): T => {
  Object.assign(error, {
    httpStatus: response.status,
    responseBody,
  });
  return error;
};

/** Processes a json response to extract the correct message string, according to the different cases.
 *
 * @param json the original json response that needs to be parsed
 * @returns the parsed error message string
 */
export function getErrorMessage(json: any) {
  if (json.error) {
    if (typeof json.error === 'string') return json.error;

    if (json.error.message) return json.error.message;
  }

  return json.errors?.toString() || JSON.stringify(json);
}

/**
 * Validates an HTTP response and throws when the response is not successful.
 *
 * For a non-2xx response, the body is read once and parsed as JSON. The helper
 * preserves the existing error types and user-facing messages (`CustomError`,
 * `CoordinatorError`, `GitRepositoryError`, or a generic `Error`) and attaches
 * `httpStatus` and `responseBody` to the thrown error. `responseBody` contains
 * the parsed JSON when available, the original text for malformed JSON, or
 * `undefined` when the body is empty/unreadable. Use
 * {@link getHttpResponseErrorMetadata} to safely retrieve these fields.
 *
 * Successful responses are left untouched, including their body. For failed
 * responses, callers must not attempt to read the body again because it has
 * already been consumed here.
 *
 * @param response HTTP response to validate
 * @throws {CustomError | CoordinatorError | GitRepositoryError | Error} when
 *   `response.ok` is false; thrown errors include `httpStatus` and
 *   `responseBody` metadata.
 */
export async function handleFailedResponse(response: Response): Promise<void> {
  if (!response.ok) {
    let responseText: string;
    try {
      responseText = await response.text();
    } catch (error) {
      throw attachHttpResponseMetadata(
        new CustomError('Unable to parse the response received', error.message),
        response,
        undefined,
      );
    }

    let json: any;
    try {
      json = JSON.parse(responseText);
    } catch (parseError) {
      throw attachHttpResponseMetadata(
        new CustomError(
          'Unable to parse the response received',
          parseError instanceof Error ? parseError.message : String(parseError),
        ),
        response,
        responseText || undefined,
      );
    }

    const error = json?.error;

    switch (error?.name) {
      case CustomErrorName.CustomError: {
        throw attachHttpResponseMetadata(
          new CustomError(error.message, error.errorMessage),
          response,
          json,
        );
      }
      case CustomErrorName.CoordinatorError: {
        throw attachHttpResponseMetadata(
          new CoordinatorError({ ...error, userMessage: error.message }),
          response,
          json,
        );
      }
      case CustomErrorName.GitRepositoryError: {
        throw attachHttpResponseMetadata(
          new GitRepositoryError({
            ...error,
            title: error.message,
            userMessage: error.errorMessage,
          }),
          response,
          json,
        );
      }
      default: {
        throw attachHttpResponseMetadata(
          new Error(getErrorMessage(json)),
          response,
          json,
        );
      }
    }
  }
}
